import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { Row } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { ConfirmCandidateSource } from '~/domain/ConfirmCandidateSource';
import { DashboardJobsStatus, useGetDashboardJobs } from '~/domain/DashboardJobs';
import { useAppSelector, useMediaQuery, useNavigateToConfirmCandidate } from '~/hooks';
import { useUserSelector } from '~/hooks/useUserSelector';
import { routes } from '~/routes';
import { CONFIRM_CANDIDATE_LABEL } from '~/utils/Enum';
import {
  fireDashboardActionNeededClicked,
  fireDashboardOpenShiftClicked,
  fireDashboardScheduledShiftClicked,
} from '~/posthogs/events';
import { SelectProfessionTypePopup } from '../ShiftRequestRedesign/SelectProfessionType/SelectProfessionTypePopup';
import { useSelectProfessionType } from '../ShiftRequestRedesign/SelectProfessionType/useSelectProfessionType';
import { BidNominationCard } from './BidNominationCard';
import { ShiftCard } from './ShiftCard';

interface IShiftsSectionProps {
  isLoading?: boolean;
}

export const ShiftsSection = ({ isLoading }: IShiftsSectionProps) => {
  const navigate = useNavigate();
  const navigateToConfirmCandidate = useNavigateToConfirmCandidate();
  const dashboardInfo = useAppSelector((state) => state.job.dashboardInfo);
  // `md`, not `lg`: the longest CTA copy ("Review before they expire", 190px)
  // still fits on one line down to a 1240px viewport, so the `lg` breakpoint
  // (1440px) collapsed the tiles to "Click" while 68px of room was still free.
  // Re-measure this breakpoint whenever the copy below gets longer.
  const [isSmallScreen] = useMediaQuery(['md']);
  const user = useUserSelector();
  const officeState = user?.address?.[0]?.state ?? '';

  const { availableProfessions } = useSelectProfessionType({ officeState });
  const [isShowPopup, setIsShowPopup] = useState(false);

  const activeCount = dashboardInfo?.activeRequest;
  const actionRequiredCount = dashboardInfo?.actionRequired;
  const scheduledCount = dashboardInfo?.scheduled;

  const { data, isLoading: isLoadingActionRequired } = useGetDashboardJobs({
    status: DashboardJobsStatus.ACTION_REQUIRED,
    page: 1,
    limit: 15,
  });

  const openShiftsCount = parseInt(String(activeCount || 0), 10);
  const actionNeededCount = parseInt(String(actionRequiredCount || 0), 10);
  const scheduledShiftsCount = parseInt(String(scheduledCount || 0), 10);

  const handleOpenShiftsClick = () => {
    fireDashboardOpenShiftClicked({ shiftCount: openShiftsCount });
    if (openShiftsCount === 0) {
      setIsShowPopup(true);
    } else {
      navigate(generatePath(routes.shiftsOverview, { type: 'open' }));
    }
  };

  const handleProfessionContinue = (professionCode: string) => {
    setIsShowPopup(false);
    navigate(routes.selectDates, { state: { professionType: professionCode } });
  };

  const handlePopupClose = () => {
    setIsShowPopup(false);
  };

  const handleActionNeededClick = () => {
    fireDashboardActionNeededClicked({ shiftCount: actionNeededCount });

    if (actionNeededCount === 1) {
      const counters = data?.[0]?.multipleCounters ?? [];
      const favoriteCandidate = counters.find((counter) => !!counter.isFavorite);

      const candidateId = (favoriteCandidate ?? counters?.[0])?.candidate?.id;
      const shiftId = data?.[0]?.id;

      if (candidateId && shiftId) {
        // Attribute a later confirm on this shift to the Action Needed card
        // (GOTU-4906) — same source as the Shifts Overview list, since both are
        // the office acting on their Action Needed queue.
        navigateToConfirmCandidate({
          shiftId,
          candidateId,
          source: ConfirmCandidateSource.ActionNeeded,
        });
        return;
      }
    }
    navigate(generatePath(routes.shiftsOverview, { type: 'pending' }));
  };

  const handleScheduledClick = () => {
    fireDashboardScheduledShiftClicked({ shiftCount: scheduledShiftsCount });
    navigate(generatePath(routes.shiftsOverview, { type: 'scheduled' }));
  };

  return (
    <>
      <Row gap={5} width="100%" wrap="wrap" className="dashboard-tiles-row">
        {actionNeededCount > 0 && (
          <ShiftCard
            title={CONFIRM_CANDIDATE_LABEL}
            count={actionNeededCount}
            barColor={colors.error[500]}
            countColor={colors.error[500]}
            ctaText={isSmallScreen ? 'Click' : 'Review before they expire'}
            isClickable
            onClick={handleActionNeededClick}
            isLoading={isLoading || isLoadingActionRequired}
            dataCy="action-needed-button"
            countDataCy="action-needed-count"
          />
        )}

        <ShiftCard
          title="Open"
          count={openShiftsCount}
          barColor={colors.success[500]}
          ctaText={
            isSmallScreen
              ? 'Click'
              : openShiftsCount === 0
                ? 'Click to post'
                : 'Invite to secure coverage'
          }
          isClickable
          onClick={handleOpenShiftsClick}
          isLoading={isLoading || isLoadingActionRequired}
          showCtaWhenEmpty
          dataCy="open-shifts-button"
          countDataCy="open-shifts-count"
        />

        <ShiftCard
          title="Scheduled"
          count={scheduledShiftsCount}
          barColor={colors.secondary[500]}
          ctaText={isSmallScreen ? 'Click' : 'View Upcoming shift'}
          isClickable={scheduledShiftsCount > 0}
          onClick={handleScheduledClick}
          isLoading={isLoading || isLoadingActionRequired}
          emptyStateMessage="Once you confirm a candidate for a posting, you'll be able to view them here."
          dataCy="scheduled-shifts-button"
          countDataCy="scheduled-shifts-count"
        />

        <BidNominationCard />
      </Row>

      {isShowPopup && (
        <SelectProfessionTypePopup
          professions={availableProfessions}
          onContinue={handleProfessionContinue}
          onClose={handlePopupClose}
        />
      )}
    </>
  );
};
